import type { PopperProps } from '@mui/base/Popper';
import { Popper } from '@mui/base/Popper';
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef, useRef } from 'react';

import { DropdownTransition } from './DropdownTransition';

const DefaultRootSlot = forwardRef<
	HTMLDivElement,
	ComponentPropsWithoutRef<'div'> & { ownerState?: unknown }
>(function DefaultRootSlotRender(props, ref) {
	const { ownerState, ...rest } = props;
	return <div {...rest} ref={ref} />;
});

interface AnimatedPopoverProps
	extends
		Pick<PopperProps, 'anchorEl' | 'open'>,
		ComponentPropsWithoutRef<'div'> {
	PopperProps?: Omit<PopperProps, 'anchorEl' | 'open' | 'transition'>;
}

export const AnimatedPopover = forwardRef<HTMLDivElement, AnimatedPopoverProps>(
	function AnimatedPopoverRender(
		{ anchorEl, open, PopperProps, children, ...forwardedProps },
		forwardedRef
	) {
		// Keep children from changing while doing a close animation. This is needed
		// because Material UI's useAutocomplete removes all options from the drop
		// down to close it.
		const childrenWhenLastOpen = useRef(children);
		if (open) {
			childrenWhenLastOpen.current = children;
		}

		/**
		 * Prevent the following error:
		 * ```
		 * The `anchorEl` prop provided to the component is invalid.
		 * It should be an HTML element instance or a virtualElement.
		 * ```
		 */
		if (!anchorEl) {
			return null;
		}

		return (
			<Popper
				{...PopperProps}
				slots={{
					root: PopperProps?.slots?.root ?? DefaultRootSlot,
				}}
				anchorEl={anchorEl}
				open={open}
				transition
			>
				{({ TransitionProps }) => (
					<DropdownTransition {...TransitionProps}>
						<div ref={forwardedRef} {...forwardedProps}>
							{childrenWhenLastOpen.current}
						</div>
					</DropdownTransition>
				)}
			</Popper>
		);
	}
);
